import { Controller } from "@hotwired/stimulus";
import { toggleVisibility } from "../../shared/toggleVisibility";

const IDENTITY_DOCUMENT_DEFAULT_VALUE = "driver_license";
const IDENTITY_DOCUMENT_MY_NUMBER_VALUE = "my_number_card";
const IDENTITY_DOCUMENT_LICENSE_RELATED_VALUES = [
  "driver_license",
  "driving_record_certificate",
];

/**
 * 本人確認書類の種類選択を管理する。
 * identity-document-type
 * app/views/secret_profiles/_form.html.erb 用。
 */
export default class extends Controller {
  static outlets = ["image-resize-preview"];
  static targets = [
    "selector",
    "backSection",
    "registeredBackSection",
    "warning",
    "notice",
    "hiddenInput",
    "backFileInput",
  ];
  static values = {
    selected: String,
    editable: { type: Boolean, default: true },
  };

  connect() {
    if (!this.hasHiddenInputTarget) return;
    this.#applySelection(this.#initialValue());
  }

  select(event) {
    const nextType = this.#readNextType(event);
    if (!nextType) return;

    this.#applySelection(nextType);

    if (this.#isMyNumber()) {
      this.#clearBackImage();
    }
  }

  #readNextType(event) {
    return event.currentTarget?.dataset?.documentType || null;
  }

  #initialValue() {
    return (
      this.hiddenInputTarget.value ||
      this.selectedValue ||
      IDENTITY_DOCUMENT_DEFAULT_VALUE
    );
  }

  #applySelection(nextValue) {
    this.selectedValue = nextValue;
    this.hiddenInputTarget.value = nextValue;
    this.#render();
  }

  #render() {
    const isMyNumber = this.#isMyNumber();
    const isLicenseRelated = this.#isLicenseRelated();
    const editable = this.editableValue;

    toggleVisibility(
      this.hasBackSectionTarget ? this.backSectionTarget : null,
      editable && !isMyNumber,
    );
    toggleVisibility(
      this.hasRegisteredBackSectionTarget
        ? this.registeredBackSectionTarget
        : null,
      editable && !isMyNumber,
    );
    toggleVisibility(
      this.hasWarningTarget ? this.warningTarget : null,
      editable && isMyNumber,
    );
    toggleVisibility(
      this.hasNoticeTarget ? this.noticeTarget : null,
      editable && isLicenseRelated,
    );
    this.#setBackInputsSubmittable(!isMyNumber);
  }

  #isMyNumber() {
    return this.selectedValue === IDENTITY_DOCUMENT_MY_NUMBER_VALUE;
  }

  #isLicenseRelated() {
    return IDENTITY_DOCUMENT_LICENSE_RELATED_VALUES.includes(
      this.selectedValue,
    );
  }

  /**
   * マイナンバーカードが選択されているかを返す。
   * drivers-license-image controller から呼び出される。
   * @returns {boolean}
   */
  isMyNumberSelected() {
    return this.#isMyNumber();
  }

  /**
   * 本人確認書類種別選択を活性化する。
   * drivers-license-image controller から画像選択解除時に再選択できるようにする
   */
  enableSelector() {
    this.editableValue = true;
    toggleVisibility(this.hasSelectorTarget ? this.selectorTarget : null, true);
    this.#render();
  }

  /**
   * 裏面セクションを表示し、入力を有効化する。
   * drivers-license-image controller から呼び出される。
   */
  showBackSection() {
    if (!this.hasBackSectionTarget) return;

    toggleVisibility(this.backSectionTarget, true);
    this.#setBackInputsSubmittable(true);
  }

  #backInputs() {
    if (!this.hasBackSectionTarget) return [];
    // section内のdrivers_license_image_backに関連する全てのinput
    return this.backSectionTarget.querySelectorAll(
      'input[name*="drivers_license_image_back"]',
    );
  }

  #setBackInputsSubmittable(submittable) {
    if (!this.hasBackSectionTarget) return;
    this.#backInputs().forEach((input) => {
      input.disabled = !submittable;
    });
  }

  #clearBackImage() {
    this.#clearBackBlobUrl();

    if (this.hasBackFileInputTarget) {
      this.backFileInputTarget.value = "";
    }

    this.#clearBackHiddenInputs();
    this.#clearBackPreview();
  }

  #clearBackBlobUrl() {
    if (!this.hasBackFileInputTarget) return;
    if (!this.backFileInputTarget.dataset.blobUrl) return;

    URL.revokeObjectURL(this.backFileInputTarget.dataset.blobUrl);
    this.backFileInputTarget.dataset.blobUrl = "";
  }

  #clearBackHiddenInputs() {
    if (!this.hasBackSectionTarget) return;

    this.backSectionTarget
      .querySelectorAll(
        'input[type="hidden"][name*="drivers_license_image_back"]',
      )
      .forEach((input) => {
        input.remove();
      });
  }

  #clearBackPreview() {
    if (!this.hasBackSectionTarget) return;

    const backController = this.imageResizePreviewOutlets.find(
      (controller) => controller.element === this.backSectionTarget,
    );
    if (backController) {
      backController.delete();
      return;
    }
    this.backSectionTarget.dataset.formStatus = "blank";
  }
}
